import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import DocumentAddView from 'features/document/DocumentAddView'
import {
  addDocument,
  addReviewDocuments,
  deleteDocument,
  DOCUMENT_FILE_SIZE_LIMIT,
  fetchChannels,
  reset,
  selectChannel,
  selectReviewDocumentType,
  setReviewDocuments,
  uploadDocumentFiles,
} from 'features/document/documentAddSlice'
import useValidator from 'utils/hooks/useValidator'
import { downloadBlob, isEmpty } from 'utils'
import { fail } from 'features/toast/Toast'
import { parse } from 'query-string'

const DocumentAdd = ({ readOnly }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { params } = useRouteMatch()
  const { search } = useLocation()
  const { from } = parse(search)
  const {
    channels,
    reviewDocumentTypes,
    selectedChannel,
    selectedReviewDocumentType,
    uploadDocuments,
  } = useSelector(state => state.document.documentAdd)

  const [files, setFiles] = useState([])

  const { validate } = useValidator(() => ({
    channel: isEmpty(selectedChannel) && '비즈채널을 선택해주세요.',
    type: isEmpty(selectedReviewDocumentType) && '심사 구분을 선택해주세요.',
    file: isEmpty(uploadDocuments) && '업로드한 파일이 없습니다.',
  }))

  useEffect(() => () => dispatch(reset()), [dispatch])

  useEffect(() => {
    dispatch(fetchChannels())
  }, [dispatch])

  useEffect(() => {
    if (selectedChannel && selectedReviewDocumentType) {
      dispatch(setReviewDocuments())
    }
  }, [selectedChannel, selectedReviewDocumentType, dispatch])

  useEffect(() => {
    const { channelId } = params
    const channel = channelId && channels.find(c => c.id === channelId)

    if (channel) {
      dispatch(selectChannel(channel))
    }
  }, [channels, reviewDocumentTypes, params, dispatch])

  const handleFileUpload = id => (file, setError) => {
    if (file.size > DOCUMENT_FILE_SIZE_LIMIT) {
      setError(true)
    } else {
      setError(false)

      const enrollTime = Date.now()
      dispatch(
        addDocument({
          id,
          size: file.size,
          name: file.name,
          fileId: enrollTime,
        })
      )
      setFiles(files.concat([{ id: enrollTime, file }]))
    }
  }

  const handleDownloadTempFile = fileId => {
    const { file } = files.find(f => f.id === fileId)
    downloadBlob(file, file.name)
  }

  const handleFileDelete = fileId => {
    setFiles(files.filter(f => f.fileId !== fileId))
    dispatch(deleteDocument(fileId))
  }

  const onClickConfirm = async () => {
    try {
      await validate()
      const { error: fileError, payload } = await dispatch(
        uploadDocumentFiles(files.map(f => f.file))
      )
      if (fileError) {
        fail(
          '일시적인 오류로 서류 등록을 진행할 수 없습니다. 잠시 후 다시 시도하세요'
        )
      } else {
        const reviewDocuments = uploadDocuments.map((d, i) => ({
          reviewRequiredDocumentId: d.id,
          type: selectedReviewDocumentType.id,
          path: payload[i].url.path,
          filename: d.name,
        }))
        const { error: documentError } = await dispatch(
          addReviewDocuments(reviewDocuments)
        )
        if (documentError) {
          fail(
            '일시적인 오류로 서류 등록을 진행할 수 없습니다. 잠시 후 다시 시도하세요'
          )
        } else {
          if (from === 'CREATIVE') {
            history.push(`/${params.accountId}/creatives/add`)
          } else {
            history.push(`/${params.accountId}/documents`)
          }
        }
      }
    } catch (errors) {
      fail(
        '일시적인 오류로 서류 등록을 진행할 수 없습니다. 잠시 후 다시 시도하세요'
      )
    }
  }

  const onClickCancel = () => {
    if (from === 'CREATIVE') {
      history.push(`/${params.accountId}/creatives/add`)
    } else {
      history.push(`/${params.accountId}/documents`)
    }
  }

  return (
    <DocumentAddView
      onClickCancel={onClickCancel}
      handleFileUpload={handleFileUpload}
      onSelectChannel={v => dispatch(selectChannel(v))}
      onSelectReviewDocumentType={v => dispatch(selectReviewDocumentType(v))}
      handleFileDelete={handleFileDelete}
      handleDownloadTempFile={handleDownloadTempFile}
      onClickConfirm={onClickConfirm}
      readOnly={readOnly}
    />
  )
}

export default DocumentAdd

DocumentAdd.propTypes = {
  readOnly: PropTypes.bool,
}
